/**
 * Evaluador de ejercicios.
 *
 * Entrada:  exerciseDef + timeline de landmarks [{ t, lm }]
 * Salida:   resumen de alertas por check con timestamp del peor frame,
 *           severidad promedio, cantidad de frames fuera de rango.
 */

import {
  LM, ALIAS, resolvePoint, angle, distance, linealidad,
  bodyHeight, centerOfMass, midpoint,
} from './landmarks.js';

// -------- Mapea el nombre de un punto a índice de landmark --------
function pointToIndex(name, side) {
  if (!name || name.endsWith('_mid')) return -1;
  if (side && ALIAS[`${name}_${side}`] != null) return ALIAS[`${name}_${side}`];
  if (ALIAS[name] != null) return ALIAS[name];
  return -1;
}

/** Devuelve los índices de landmark involucrados en un check. */
export function checkLandmarkIndices(check) {
  const out = new Set();
  if (check.type === 'apertura_piernas') {
    out.add(LM.L_ANKLE); out.add(LM.R_ANKLE);
    out.add(LM.L_HIP); out.add(LM.R_HIP);
    return out;
  }
  const sides =
    check.type === 'simetria_angulo' ? ['L', 'R'] :
    check.side === 'any' ? ['L', 'R'] :
    check.side ? [check.side] : ['L', 'R'];
  for (const side of sides) {
    for (const p of check.points || []) {
      const i = pointToIndex(p, side);
      if (i >= 0) out.add(i);
    }
  }
  return out;
}

// --------------------- Detector de apex (cadera más arriba) ---------------------
export function detectApexFrames(timeline) {
  // En MediaPipe Y=0 arriba, Y=1 abajo → "más alto" = y menor
  if (timeline.length < 5) return [];
  const ys = timeline.map(f => {
    const com = centerOfMass(f.lm);
    return com ? com.y : 0.5;
  });

  // Buscar mínimos locales con ventana de 3 frames
  const apex = [];
  for (let i = 2; i < ys.length - 2; i++) {
    if (ys[i] < ys[i - 2] && ys[i] < ys[i + 2] && ys[i] < ys[i - 1] && ys[i] < ys[i + 1]) {
      apex.push(i);
    }
  }
  if (apex.length === 0) {
    // fallback: el mínimo global
    let minI = 0;
    for (let i = 1; i < ys.length; i++) if (ys[i] < ys[minI]) minI = i;
    apex.push(minI);
  }
  return apex;
}

// --------------------- Evaluadores por tipo ---------------------
function evalAngulo(check, lm) {
  const sides = check.side === 'any' ? ['L', 'R'] : [check.side];
  let best = { value: NaN, severity: 0 };
  for (const side of sides) {
    const [a, b, c] = check.points.map(n => resolvePoint(lm, n, side));
    if (!a || !b || !c) continue;
    const v = angle(a, b, c);
    if (!Number.isFinite(v)) continue;
    const sev = severityFromAngle(v, check);
    if (sev > best.severity) best = { value: v, severity: sev, side };
  }
  return best;
}

function severityFromAngle(value, check) {
  const { ideal, warn, critical, direction = 'lower_is_worse' } = check;
  if (direction === 'lower_is_worse') {
    if (value >= warn) return 0;
    if (value <= critical) return 1;
    return (warn - value) / (warn - critical);
  }
  // higher_is_worse
  if (value <= warn) return 0;
  if (value >= critical) return 1;
  return (value - warn) / (critical - warn);
}

function evalLinealidad(check, lm, bh) {
  const sides = check.side === 'any' ? ['L', 'R'] : [check.side || 'L'];
  let best = { value: 0, severity: 0 };
  for (const side of sides) {
    const pts = check.points.map(n => resolvePoint(lm, n, side));
    if (pts.some(p => !p)) continue;
    const dev = linealidad(pts, bh);
    const sev = dev <= check.warn ? 0
      : dev >= check.critical ? 1
      : (dev - check.warn) / (check.critical - check.warn);
    if (sev > best.severity) best = { value: dev, severity: sev, side };
  }
  return best;
}

function evalDistanciaNorm(check, lm, bh) {
  const [an, bn] = check.points;
  // soportar 'ankle_L' directo o 'ankle' con side
  const a = lm[ALIAS[an]] ?? resolvePoint(lm, an);
  const b = lm[ALIAS[bn]] ?? resolvePoint(lm, bn);
  if (!a || !b) return { value: NaN, severity: 0 };
  const d = distance(a, b) / Math.max(bh, 1e-6);
  const sev = d <= check.warn ? 0
    : d >= check.critical ? 1
    : (d - check.warn) / (check.critical - check.warn);
  return { value: d, severity: sev };
}

function evalSimetriaAngulo(check, lm) {
  const [a, b, c] = check.points;
  const angL = angle(resolvePoint(lm, a, 'L'), resolvePoint(lm, b, 'L'), resolvePoint(lm, c, 'L'));
  const angR = angle(resolvePoint(lm, a, 'R'), resolvePoint(lm, b, 'R'), resolvePoint(lm, c, 'R'));
  if (!Number.isFinite(angL) || !Number.isFinite(angR)) return { value: NaN, severity: 0 };
  const diff = Math.abs(angL - angR);
  const sev = diff <= check.toleranceDeg ? 0
    : diff >= check.criticalDeg ? 1
    : (diff - check.toleranceDeg) / (check.criticalDeg - check.toleranceDeg);
  return { value: diff, severity: sev };
}

function evalAperturaPiernas(check, lm) {
  const ankleL = lm[LM.L_ANKLE], ankleR = lm[LM.R_ANKLE];
  const hipMid = midpoint(lm[LM.L_HIP], lm[LM.R_HIP]);
  if (!ankleL || !ankleR || !hipMid) return { value: NaN, severity: 0 };
  const v = angle(ankleL, hipMid, ankleR);
  if (!Number.isFinite(v)) return { value: NaN, severity: 0 };
  return { value: v, severity: severityFromAngle(v, check) };
}

function evalCheck(check, lm, bh) {
  switch (check.type) {
    case 'angulo': return evalAngulo(check, lm);
    case 'linealidad': return evalLinealidad(check, lm, bh);
    case 'distancia_norm': return evalDistanciaNorm(check, lm, bh);
    case 'simetria_angulo': return evalSimetriaAngulo(check, lm);
    case 'apertura_piernas': return evalAperturaPiernas(check, lm);
    default: return { value: NaN, severity: 0 };
  }
}

// --------------------- Evaluador principal ---------------------
/**
 * @param {object} exercise   Definición del ejercicio.
 * @param {Array}  timeline   [{ t: number, lm: Pt[] }]
 * @returns {object}          { alerts: [...], summary: {...} }
 */
export function evaluate(exercise, timeline) {
  if (!timeline.length || !exercise) return { alerts: [], summary: null };

  const apexSet = exercise.useApexDetector
    ? new Set(detectApexFrames(timeline))
    : null;

  // Acumulador por check
  const acc = new Map();
  for (const c of exercise.checks) {
    acc.set(c.id, {
      check: c,
      framesEvaluated: 0,
      framesOut: 0,
      severitySum: 0,
      worst: { severity: 0, value: NaN, t: 0, frameIdx: 0, side: null },
    });
  }

  timeline.forEach((frame, i) => {
    const bh = bodyHeight(frame.lm);
    for (const check of exercise.checks) {
      if (check.evaluateOnlyAtApex && apexSet && !apexSet.has(i)) continue;
      const r = evalCheck(check, frame.lm, bh);
      if (!Number.isFinite(r.value)) continue;
      const a = acc.get(check.id);
      a.framesEvaluated += 1;
      if (r.severity > 0) {
        a.framesOut += 1;
        a.severitySum += r.severity;
        if (r.severity > a.worst.severity) {
          a.worst = { severity: r.severity, value: r.value, t: frame.t, frameIdx: i, side: r.side };
        }
      }
    }
  });

  const alerts = [];
  for (const [, a] of acc) {
    if (a.framesEvaluated === 0) continue;
    const outRatio = a.framesOut / a.framesEvaluated;
    if (outRatio < 0.15 && a.worst.severity < 0.5) continue; // poca evidencia → ignora
    const avgSeverity = a.severitySum / Math.max(1, a.framesOut);
    alerts.push({
      id: a.check.id,
      name: a.check.name,
      message: a.check.message,
      drill: a.check.drill,
      type: a.check.type,
      outRatio,
      framesOut: a.framesOut,
      framesTotal: a.framesEvaluated,
      avgSeverity,
      worst: a.worst,
      level: a.worst.severity >= 0.8 ? 'critical' : a.worst.severity >= 0.4 ? 'warning' : 'info',
    });
  }

  // Orden: críticos primero, luego por severidad
  alerts.sort((x, y) => {
    const order = { critical: 0, warning: 1, info: 2 };
    return order[x.level] - order[y.level] || y.worst.severity - x.worst.severity;
  });

  const summary = {
    exerciseId: exercise.id,
    exerciseName: exercise.name,
    totalFrames: timeline.length,
    alertCount: alerts.length,
    criticalCount: alerts.filter(a => a.level === 'critical').length,
    warningCount: alerts.filter(a => a.level === 'warning').length,
    score: Math.max(0, 100 - alerts.reduce((s, a) => s + a.avgSeverity * 20, 0)),
  };

  return { alerts, summary };
}

/**
 * Evaluación por frame único (para overlay en tiempo real durante la grabación).
 * Devuelve [{ check, value, severity, side, landmarks: Set<number> }], sólo los
 * checks con severidad > minSeverity. Los checks apex-only se ignoran aquí.
 */
export function evaluateFrame(exercise, lm, minSeverity = 0.3) {
  if (!exercise || !lm) return [];
  const bh = bodyHeight(lm);
  const out = [];
  for (const check of exercise.checks) {
    if (check.evaluateOnlyAtApex) continue;
    const r = evalCheck(check, lm, bh);
    if (!Number.isFinite(r.value) || r.severity <= minSeverity) continue;
    out.push({
      check,
      value: r.value,
      severity: r.severity,
      side: r.side,
      landmarks: checkLandmarkIndices(check),
    });
  }
  out.sort((a, b) => b.severity - a.severity);
  return out;
}
